import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/shared/SignedAvatarImage';
import { useAuth } from '@/contexts/AuthContext';
import { useCreateProfile } from '@/hooks/useProfile';
import { useJoinCohort, useCohorts } from '@/hooks/useCohorts';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2, CheckCircle, MapPin, Linkedin } from 'lucide-react';

const CITY_COHORTS = [
  'Sydney',
  'Melbourne',
  'Brisbane',
  'Auckland',
  'New York',
  'San Francisco',
  'Los Angeles',
  'London',
  'Toronto',
  'Singapore',
];

export default function LinkedInOnboardingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const createProfile = useCreateProfile();
  const { data: cohorts } = useCohorts();
  const joinCohort = useJoinCohort();

  const metadata = user?.user_metadata ?? {};
  const linkedInName = metadata.full_name || metadata.name || '';
  const linkedInAvatar = metadata.avatar_url || metadata.picture || '';

  const [fullName, setFullName] = useState(linkedInName);
  const [cityCohort, setCityCohort] = useState('Sydney');
  const [inviteCode, setInviteCode] = useState('');
  const [inviteVerified, setInviteVerified] = useState(false);
  const [loading, setLoading] = useState(false);

  // Read invite code from localStorage (set before OAuth redirect)
  useEffect(() => {
    const storedCode = localStorage.getItem('qbn_invite_code');
    if (storedCode) {
      setInviteCode(storedCode);
      setInviteVerified(true);
    }
  }, []);

  const verifyInviteCode = async () => {
    setLoading(true);
    try {
      // Use secure RPC function to validate code without exposing table data
      const { data, error } = await supabase
        .rpc('validate_invite_code', { code_to_check: inviteCode.toUpperCase() });

      if (error) throw error;

      if (data && data.length > 0 && data[0].is_valid) {
        setInviteVerified(true);
        toast({ title: 'Invite code verified' });
      } else {
        toast({
          title: 'Invalid invite code',
          description: 'This code is invalid or has already been used.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!fullName.trim()) {
      toast({ title: 'Name required', description: 'Please enter your name.', variant: 'destructive' });
      return;
    }
    if (!inviteVerified) {
      toast({ title: 'Invite code required', description: 'Please verify your invite code.', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      // Consume invite code using secure RPC function
      if (user?.id) {
        await supabase.rpc('use_invite_code', {
          code_to_use: inviteCode.toUpperCase(),
          user_id_using: user.id,
        });
      }

      // Create profile with LinkedIn data
      await createProfile.mutateAsync({
        full_name: fullName.trim(),
        avatar_url: linkedInAvatar || null,
        city_cohort: cityCohort,
        headline: metadata.headline || null,
        pronouns: null,
        company: null,
        role: null,
        industry: null,
        linkedin_url: null,
        bio: null,
        is_mentor: true,
        onboarding_completed: true,
      });

      // Auto-join cohort
      const matchingCohort = cohorts?.find(
        (c) =>
          c.city?.toLowerCase() === cityCohort.toLowerCase() ||
          c.name.toLowerCase() === cityCohort.toLowerCase()
      );
      if (matchingCohort) {
        await joinCohort.mutateAsync({ cohortId: matchingCohort.id, isPrimary: true });
      }

      // Cleanup
      localStorage.removeItem('qbn_invite_code');

      toast({
        title: 'Welcome to QBN!',
        description: 'Your profile is set up. Start connecting with your network.',
      });
      navigate('/home', { replace: true });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const isSubmitting = createProfile.isPending || joinCohort.isPending || loading;

  return (
    <div className="min-h-screen bg-gradient-hero flex flex-col p-6">
      <div className="flex-1 flex items-center justify-center">
        <Card className="w-full max-w-lg bg-card/95 backdrop-blur border-border">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Avatar className="h-20 w-20">
                <SignedAvatarImage src={linkedInAvatar || undefined} />
                <AvatarFallback className="bg-gradient-primary text-white text-2xl font-bold">
                  {fullName?.charAt(0) || '?'}
                </AvatarFallback>
              </Avatar>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mb-2">
              <Linkedin className="h-4 w-4" />
              <span>Signed in with LinkedIn</span>
            </div>
            <CardTitle className="text-xl">Almost there!</CardTitle>
            <CardDescription>
              Just a couple more details to complete your profile
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Name (pre-filled, editable) */}
            <div className="space-y-2">
              <Label htmlFor="full_name">Full Name *</Label>
              <Input
                id="full_name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>

            {/* City Cohort */}
            <div className="space-y-2">
              <Label htmlFor="city_cohort">
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  Location *
                </span>
              </Label>
              <Select value={cityCohort} onValueChange={setCityCohort}>
                <SelectTrigger>
                  <SelectValue placeholder="Select your city" />
                </SelectTrigger>
                <SelectContent>
                  {CITY_COHORTS.map((city) => (
                    <SelectItem key={city} value={city}>
                      {city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Invite Code */}
            {inviteVerified ? (
              <div className="flex items-center gap-2 p-3 bg-emerald-500/10 rounded-lg">
                <CheckCircle className="h-4 w-4 text-emerald-500" />
                <span className="text-sm text-emerald-600 dark:text-emerald-400">
                  Invite code verified
                </span>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="invite_code">Invite Code *</Label>
                <div className="flex gap-2">
                  <Input
                    id="invite_code"
                    placeholder="Enter your invite code"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                    className="font-mono tracking-wider"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={verifyInviteCode}
                    disabled={loading || !inviteCode}
                  >
                    Verify
                  </Button>
                </div>
              </div>
            )}

            {/* Submit */}
            <Button
              className="w-full btn-qbn-gradient"
              onClick={handleComplete}
              disabled={isSubmitting || !inviteVerified || !fullName.trim()}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Setting up...
                </>
              ) : (
                'Complete Setup'
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
